import {
  MappedField,
  SortColumn,
} from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { hasAllowedFields } from './field-structure.utils';

export type ShipmentFields = {
  /**
   * Required for commit form
   */
  controlType?: ShipmentPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: ShipmentPlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: ShipmentPlaceHolderTypeVal;
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: ShipmentPlaceHolderTypeVal;

  /**
   * Used as field in forms
   */
  id: ShipmentPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: ShipmentPlaceHolderTypeVal;
  /**
   * Adds field explicitly in datagrid columns
   * */
  mandatory?: boolean;

  /**
   * Shows if it should be visible by default in forms
   */
  visible?: ShipmentPlaceHolderTypeVal;
};

export type ShipmentPlaceHolderTypeVal = {
  createDocsForm?: boolean | number | string;
  createForm?: boolean | number | string;
  createIntDocsForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editForm?: boolean | number | string;
};

export type ShipmentProperty = {
  id: string;
  name: string;
  type: 'boolean' | 'custom' | 'date' | 'dropdown' | 'number' | 'string';
};

export enum EnumShipmentsViews {
  DETAILS = 0,
  DOCUMENTS = 1,
  INTERNAL_DOCUMENTS = 2,
  INSTRUCTIONS = 3,
  HISTORY = 4,
}

export enum EnumShipmentFieldStructure {
  ShipmentTable = 0,
  NestedDetailsTable = 1,
  NestedDocsTable = 2,
  NestedDocsCreateForm = 3,
  NestedIntDocsTable = 4,
  NestedIntDocsCreateForm = 5,
  NestedInstructionTable = 6,
  NestedHistoryTable = 7,
}

export interface ShipmentDeliveriesDetail {
  actualETADate?: string;
  arrivalDate?: string;
  commodityCode?: string;
  containerNumber?: string;
  countryOfOrigin?: string;
  forwarder?: string;
  inboundDeliveryNumber?: string;
  invoiceNumber?: string;
  materialGroupDuty?: string;
  partNumber?: string;
  purchaseOrderItem?: string;
  purchaseOrderNumber?: string;
  quantity?: number;
  receiveDate?: string;
  slotCode?: string;
  slotDate?: string;
  trackNumber?: string;
  typeOfTransportation?: string;
  vendorCode?: string;
  vendorName?: string;
}

type DeliveryInfo = {
  date: string;
  isApproved: boolean;
  regime: string;
};

// export type Shipment = {
//   containerNumber?: string;
//   flags?: string[];
//   id: string;
//   instructionRequestDate?: string;
//   instructionSendDate?: string;
//   instructionType?: string;
//   key?: string;
//   lastModifiedBy?: string;
//   lastModifiedDate?: string;
//   mrnNumber?: string;
//   receiveDate?: string;
//   requestNumber?: string;
//   status?: string;
//   trackNumber?: string;
// };

type DateSuffix = 'From' | 'To';

export type DynamicDateKey = `${string}${DateSuffix}`;

export type ShipmentFilterUIModel = {
  contacts?: string[];
  containerNumbers?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  dateType?: string;
  fields?: string[];
  flags?: string[];
  invoiceNumbers?: string[];
  onlyMyShipments?: boolean;
  plants?: string[];
  requestNumbers?: string[];
  slotCodes?: string[];
  sorts?: SortColumn[];
  status?: string[];
  trackNumbers?: string[];
  type?: string;
  vendorCodes?: string[];
  warehouses?: string[];
} & {
  [K in DynamicDateKey]?: string;
};

export type UploadDocument = {
  content: File[];
};

export type UploadShipmentDocumentPayload = UploadDocument & {
  carrierEtaDate?: string;
  purchaseOrderNumber: string;
  types: string;
};

export type UploadShipmentInternalDocumentPayload = UploadDocument & {
  description: string;
};

export type InstructionPayload = {
  [key: string]: boolean | number | string | string[] | undefined;
  date: string;
  regime: string;
};

export type InstructionDetail = {
  canCopyToHeader?: boolean;
  commodityCode: string;
  commodityCodeDesc: string;
  deliveryTerm: string;
  id: string;
  instructionType?: string;
  invoiceNumber: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
  materialGroupDuty: string;
  partNumber?: string;
  purchaseOrderNumber: string;
  storageLocation: string;
};

export type EmailRecipients = {
  emailTo: string[];
  emailToBCC: string[];
  emailToCC: string[];
};

export type TemplatePayload = {
  body: string;
  recipients: EmailRecipients;
  requestApprove?: boolean;
  subject: string;
};

export type TemplateDetail = Pick<InstructionDetail, 'id' | 'instructionType'> &
  Partial<EmailRecipients> &
  Partial<InstructionPayload> & {
    body?: string;
    deliveryInfo?: DeliveryInfo;
    recipients?: EmailRecipients;
    subject?: string;
  };

export type ShipmentsFilterRequest = {
  customerQuery?: string;
  filterQuery: string;
  plants: string;
  status: string[];
};

export type DownloadProps = {
  fields: string[];
  ids: string[];
};

export class ShipmentListField {
  fields: MappedField[];
  userRightFields!: string[];
  hasField: (
    field: ShipmentFields,
    refId: keyof ShipmentPlaceHolderTypeVal,
    actualId: keyof ShipmentPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumShipmentFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  getDefaultFields(type?: EnumShipmentFieldStructure): MappedField[] {
    switch (type) {
      case EnumShipmentFieldStructure.ShipmentTable:
        return this.getShipmentFields();
      case EnumShipmentFieldStructure.NestedDetailsTable:
        return this.getShipmentDetailsFields();
      case EnumShipmentFieldStructure.NestedDocsTable:
        return this.getShipmentDocsFields();
      case EnumShipmentFieldStructure.NestedDocsCreateForm:
        return this.getCreateShipmentDocsFormFields();
      case EnumShipmentFieldStructure.NestedIntDocsTable:
        return this.getShipmentIntDocsFields();
      case EnumShipmentFieldStructure.NestedIntDocsCreateForm:
        return this.getCreateShipmentIntDocsFormFields();
      case EnumShipmentFieldStructure.NestedInstructionTable:
        return this.getShipmentInstructionsFields();
      case EnumShipmentFieldStructure.NestedHistoryTable:
        return this.getShipmentHistoryFields();
      default:
        return this.getShipmentFields();
    }
  }

  /**
   *
   * @param field field which inclusiveness needs to check
   * @param refId Id
   * @param actualId actual Id on which fields belongs
   * @returns is valid field
   */
  validFunWithUserFields = (
    field: ShipmentFields,
    refId: keyof ShipmentPlaceHolderTypeVal,
    actualId: keyof ShipmentPlaceHolderTypeVal,
  ): boolean => {
    if (
      this.userRightFields &&
      !(field.isRequired && field.isRequired[actualId]) &&
      !field.mandatory //For adding fields not present in default fields
    ) {
      // return this.userRightFields.includes((<string>field.id[refId]).toLowerCase());
      return (
        this.userRightFields.includes((<string>field.id[refId]).toLowerCase()) &&
        !(field.exclude && field.exclude[actualId] == true)
      ); //2571 Comment
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };
  validFunWithoutUserFields = (
    field: ShipmentFields,
    refId: keyof ShipmentPlaceHolderTypeVal,
    actualId: keyof ShipmentPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && field.exclude[actualId] == true) {
      return false;
    } else {
      return true;
    }
  };

  getShipmentFields(): MappedField[] {
    return this.shipmentField
      .filter((field: ShipmentFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: ShipmentFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          mandatory: !!field.mandatory,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly shipmentField: ShipmentFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'flag' },
      headerText: { datagrid: 'Flags' },
      id: { datagrid: 'flags' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'ID' },
      id: { datagrid: 'id' },
      visible: { datagrid: false },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Buyer' },
      id: { datagrid: 'buyer' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Cost Center' },
      id: { datagrid: 'costCenter' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Transport Mode' },
      id: { datagrid: 'transportMode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Logistic' },
      id: { datagrid: 'logistic' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Warehouse' },
      id: { datagrid: 'warehouse' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Forwarder' },
      id: { datagrid: 'forwarder' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Key' },
      id: { datagrid: 'key' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Request Number' },
      id: { datagrid: 'requestNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Track Number' },
      id: { datagrid: 'trackNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Regime' },
      id: { datagrid: 'dutyType' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Container Number' },
      id: { datagrid: 'containerNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'MRN Number' },
      id: { datagrid: 'mrnNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Invoice Number' },
      id: { datagrid: 'invoiceNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Plant' },
      id: { datagrid: 'plant' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Status' },
      id: { datagrid: 'status' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Actual ETA Date' },
      id: { datagrid: 'actualETADate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Carrier ETA Date' },
      id: { datagrid: 'carrierETADate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Instruction ETA Date' },
      id: { datagrid: 'instructionETADate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Receipt Date' },
      id: { datagrid: 'receiveDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Instruction Type' },
      id: { datagrid: 'instructionType' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Instruction Request By' },
      id: { datagrid: 'instructionRequestedBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Instruction Request Date' },
      id: { datagrid: 'instructionRequestDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Instruction Send Date' },
      id: { datagrid: 'instructionSendDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Last Modified Date' },
      id: { datagrid: 'lastModifiedDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Last Modified By' },
      id: { datagrid: 'lastModifiedBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'downloadPdf' },
      headerText: { datagrid: 'Download PDF' },
      id: { datagrid: 'caninvoicebedownloaded' },
      visible: { datagrid: true },
      //mandatory: true
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Slot Code' },
      id: { datagrid: 'slotCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Slot Date' },
      id: { datagrid: 'slotDate' },
      visible: { datagrid: true },
    },
  ];

  getShipmentDetailsFields(): MappedField[] {
    return this.shipmentDetailsField
      .filter((field: ShipmentFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: ShipmentFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly shipmentDetailsField: ShipmentFields[] = [
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Inbound Delivery Number' },
      id: { datagrid: 'inboundDeliveryNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Track Number' },
      id: { datagrid: 'trackNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Container Number' },
      id: { datagrid: 'containerNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Invoice Number' },
      id: { datagrid: 'invoiceNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Purchase Order Number' },
      id: { datagrid: 'purchaseOrderNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Purchase Order Line' },
      id: { datagrid: 'purchaseOrderItem' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Part Number' },
      id: { datagrid: 'partNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Name' },
      id: { datagrid: 'vendorName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Quantity' },
      id: { datagrid: 'quantity' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Regime' },
      id: { datagrid: 'materialGroupDuty' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'HSCode' },
      id: { datagrid: 'commodityCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Forwarder' },
      id: { datagrid: 'forwarder' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Actual ETA Date' },
      id: { datagrid: 'actualETADate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Arrival Date' },
      id: { datagrid: 'arrivalDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Slot Code' },
      id: { datagrid: 'slotCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Slot Date' },
      id: { datagrid: 'slotDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Receipt Date' },
      id: { datagrid: 'receiveDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'COO' },
      id: { datagrid: 'countryOfOrigin' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Transport Mode' },
      id: { datagrid: 'typeOfTransportation' },
      visible: { datagrid: true },
    },
  ];

  getShipmentDocsFields(): MappedField[] {
    return this.shipmentDocsField
      .filter((field: ShipmentFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: ShipmentFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getCreateShipmentDocsFormFields(): MappedField[] {
    return this.shipmentDocsField
      .filter((field: ShipmentFields): boolean => {
        return this.hasField(field, 'datagrid', 'createDocsForm');
      })
      .map((field: ShipmentFields): MappedField => {
        return {
          controlType: field.controlType?.createDocsForm
            ? (field.controlType.createDocsForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.createDocsForm as string) ??
            (field.headerText?.datagrid as string) ??
            '',
          isRequired: !!field.isRequired?.createDocsForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly shipmentDocsField: ShipmentFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createDocsForm: 'file' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Document Name' },
      id: { datagrid: 'documentName' },
      visible: { datagrid: true },
    },

    {
      controlType: { createDocsForm: 'false' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Document Type' },
      id: { datagrid: 'documentType' },
      visible: { datagrid: true },
    },

    // {
    //   id: { datagrid: 'purchaseNumber' },
    //   headerText: { datagrid: 'Purchase Number', createDocsForm: 'Purchase Numbers'},
    //   controlType: { createDocsForm: 'simple' },
    //   dataType: { datagrid: 'string' },
    //   visible: { datagrid: false },
    // },
    {
      controlType: { createDocsForm: 'dropdown' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Types' },
      id: { datagrid: 'types' },
      isRequired: { createDocsForm: true },
      visible: { datagrid: false },
    },
  ];

  getShipmentIntDocsFields(): MappedField[] {
    return this.shipmentIntDocsField
      .filter((field: ShipmentFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: ShipmentFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getCreateShipmentIntDocsFormFields(): MappedField[] {
    return this.shipmentIntDocsField
      .filter((field: ShipmentFields): boolean => {
        return this.hasField(field, 'datagrid', 'createIntDocsForm');
      })
      .map((field: ShipmentFields): MappedField => {
        return {
          controlType: field.controlType?.createIntDocsForm
            ? (field.controlType.createIntDocsForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.createIntDocsForm as string) ??
            (field.headerText?.datagrid as string) ??
            '',
          isRequired: !!field.isRequired?.createIntDocsForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly shipmentIntDocsField: ShipmentFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createIntDocsForm: 'file' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Document Name' },
      id: { datagrid: 'documentName' },
      visible: { datagrid: true },
    },
    {
      controlType: { createIntDocsForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Description' },
      id: { datagrid: 'description' },
      visible: { datagrid: true },
    },
  ];

  getShipmentInstructionsFields(): MappedField[] {
    return this.shipmentInstructionsField
      .filter((field: ShipmentFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: ShipmentFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly shipmentInstructionsField: ShipmentFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'checkbox' },
      headerText: { datagrid: 'Active' },
      id: { datagrid: 'active' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Status' },
      id: { datagrid: 'status' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Instruction Type' },
      id: { datagrid: 'instructionType' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Purchase Order Number' },
      id: { datagrid: 'purchaseOrderNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Storage Location' },
      id: { datagrid: 'storageLocation' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Delivery Term' },
      id: { datagrid: 'deliveryTerm' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Carrier ETA Date' },
      id: { datagrid: 'date' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Carrier Note' },
      id: { datagrid: 'regime' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Request Date' },
      id: { datagrid: 'requestDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Send Date' },
      id: { datagrid: 'sendDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Last Modified Date' },
      id: { datagrid: 'lastModifiedDate' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Last Modified By' },
      id: { datagrid: 'lastModifiedBy' },
      visible: { datagrid: true },
    },
    // {
    //   id: { datagrid: 'isFailed' },
    //   headerText: { datagrid: 'Is Failed'},
    //   dataType: { datagrid: 'checkbox' },
    //   visible: { datagrid: true },
    // },
    // {
    //   id: { datagrid: 'isSend' },
    //   headerText: { datagrid: 'Is Send'},
    //   dataType: { datagrid: 'checkbox' },
    //   visible: { datagrid: true },
    // },
  ];

  getShipmentHistoryFields(): MappedField[] {
    return this.shipmentHistoryField
      .filter((field: ShipmentFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: ShipmentFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly shipmentHistoryField: ShipmentFields[] = [
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Id' },
      id: { datagrid: 'id' },
      visible: { datagrid: false },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Key' },
      id: { datagrid: 'key' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Type' },
      id: { datagrid: 'type' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Text' },
      id: { datagrid: 'text' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Date Created' },
      id: { datagrid: 'dateCreated' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Created By' },
      id: { datagrid: 'createdBy' },
      visible: { datagrid: true },
    },
  ];
}
